package br.com.vvrefeicoes.portal.feature.juridico

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.JuridicoEmpresa
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Search

@Composable
fun JuridicoEmpresasScreen() {
    val controller = rememberJuridicoEmpresasController()
    LaunchedEffect(controller) { controller.init() }
    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        footer = { AppFooter() },
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space20),
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
            Column {
                Text("Empresas CNPJ", style = MaterialTheme.typography.headlineMedium)
                Text("Cadastro de empresas para o Jurídico", color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            AppButton("Nova empresa", { controller.openCreate() }, leadingIcon = com.composables.icons.lucide.Lucide.Plus)
        }
        Surface(shape = MaterialTheme.shapes.medium, tonalElevation = 1.dp, modifier = Modifier.fillMaxWidth()) {
            Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                Text("Filtros", style = MaterialTheme.typography.titleMedium)
                BoxWithConstraints(Modifier.fillMaxWidth()) {
                    if (maxWidth < 720.dp) {
                        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                            AppTextField(controller.nomeFiltro, { controller.nomeFiltro = it }, label = "Nome", modifier = Modifier.fillMaxWidth())
                            AppTextField(controller.cnpjFiltro, { controller.cnpjFiltro = it }, label = "CNPJ", modifier = Modifier.fillMaxWidth())
                            AppTextField(controller.tipoFiltro, { controller.tipoFiltro = it }, label = "Tipo", modifier = Modifier.fillMaxWidth())
                        }
                    } else {
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                            AppTextField(controller.nomeFiltro, { controller.nomeFiltro = it }, label = "Nome", modifier = Modifier.weight(1f))
                            AppTextField(controller.cnpjFiltro, { controller.cnpjFiltro = it }, label = "CNPJ", modifier = Modifier.weight(1f))
                            AppTextField(controller.tipoFiltro, { controller.tipoFiltro = it }, label = "Tipo", modifier = Modifier.weight(1f))
                        }
                    }
                }
                StatusFilter(controller)
                AppButton("Aplicar filtros", controller::applyFilters, variant = PortalButtonVariant.Secondary, leadingIcon = com.composables.icons.lucide.Lucide.Search)
            }
        }
        if (controller.errorMessage != null) {
            Text(controller.errorMessage!!, color = MaterialTheme.colorScheme.error)
        }
        when {
            controller.loading -> Box(Modifier.fillMaxWidth().padding(AppSpacing.space48), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
            controller.empresas.isEmpty() -> EmptyState(message = "Nenhuma empresa encontrada. Ajuste os filtros ou cadastre uma nova empresa.")
            else -> BoxWithConstraints(Modifier.fillMaxWidth()) { EmpresaTable(controller.empresas, controller, maxWidth < 720.dp) }
        }
        AppPagination(controller.page, controller.totalPages, controller::goToPage, Modifier.align(Alignment.CenterHorizontally))
    }
    if (controller.formOpen) {
        EmpresaForm(controller)
    }
}

@Composable
private fun StatusFilter(controller: JuridicoEmpresasController) {
    var expanded by remember { mutableStateOf(false) }
    val label =
        when (controller.statusFiltro) {
            true -> "Ativas"
            false -> "Inativas"
            null -> "Todos os status"
        }
    Box {
        AppButton(label, { expanded = true }, variant = PortalButtonVariant.Outlined)
        DropdownMenu(expanded, { expanded = false }) {
            listOf("Ativas" to true, "Inativas" to false, "Todos os status" to null).forEach { (text, value) ->
                DropdownMenuItem(text = { Text(text) }, onClick = {
                    controller.statusFiltro = value
                    expanded = false
                })
            }
        }
    }
}

@Composable
private fun EmpresaTable(
    empresas: List<JuridicoEmpresa>,
    controller: JuridicoEmpresasController,
    compact: Boolean,
) {
    Surface(shape = MaterialTheme.shapes.medium, tonalElevation = 1.dp, modifier = Modifier.fillMaxWidth()) {
        Column {
            if (!compact) {
                Row(Modifier.fillMaxWidth().padding(AppSpacing.space16), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    TableHeader("Nome", 1.5f)
                    TableHeader("CNPJ", 1.2f)
                    TableHeader("Tipo", 1f)
                    TableHeader("Status", .8f)
                    TableHeader("", .4f)
                }
            }
            empresas.forEach { empresa ->
                if (compact) CompactEmpresaRow(empresa, controller) else DesktopEmpresaRow(empresa, controller)
            }
        }
    }
}

@Composable
private fun CompactEmpresaRow(
    empresa: JuridicoEmpresa,
    controller: JuridicoEmpresasController,
) {
    Column(Modifier.fillMaxWidth().padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
            Text(empresa.nome, style = MaterialTheme.typography.titleMedium)
            EmpresaActions(empresa, controller, Modifier)
        }
        Text(formatCnpj(empresa.cnpj), color = MaterialTheme.colorScheme.onSurfaceVariant)
        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8), verticalAlignment = Alignment.CenterVertically) {
            Text(empresa.tipo)
            AppBadge(if (empresa.ativo) "Ativa" else "Inativa", if (empresa.ativo) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant, if (empresa.ativo) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun DesktopEmpresaRow(
    empresa: JuridicoEmpresa,
    controller: JuridicoEmpresasController,
) {
    Row(Modifier.fillMaxWidth().padding(AppSpacing.space16), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(empresa.nome, modifier = Modifier.weight(1.5f))
        Text(formatCnpj(empresa.cnpj), modifier = Modifier.weight(1.2f), color = MaterialTheme.colorScheme.onSurfaceVariant)
        Text(empresa.tipo, modifier = Modifier.weight(1f))
        AppBadge(if (empresa.ativo) "Ativa" else "Inativa", if (empresa.ativo) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant, if (empresa.ativo) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant, Modifier.weight(.8f))
        EmpresaActions(empresa, controller, Modifier.weight(.4f))
    }
}

@Composable
private fun RowScope.TableHeader(
    text: String,
    weight: Float,
) {
    Text(text, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.weight(weight))
}

@Composable
private fun EmpresaActions(
    empresa: JuridicoEmpresa,
    controller: JuridicoEmpresasController,
    modifier: Modifier,
) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier) {
        IconButton({ expanded = true }) { Icon(com.composables.icons.lucide.Lucide.EllipsisVertical, contentDescription = "Ações") }
        DropdownMenu(expanded, { expanded = false }) {
            DropdownMenuItem(text = { Text("Editar") }, onClick = {
                expanded = false
                controller.openEdit(empresa)
            })
            DropdownMenuItem(text = { Text(if (empresa.ativo) "Desativar" else "Ativar") }, onClick = {
                expanded = false
                controller.toggleStatus(empresa)
            })
        }
    }
}

@Composable
private fun EmpresaForm(controller: JuridicoEmpresasController) {
    val editing = controller.formEmpresa != null
    AppModalCard(if (editing) "Editar empresa" else "Nova empresa", controller::closeForm) {
        AppTextField(controller.formNome, { controller.formNome = it }, label = "Nome", modifier = Modifier.widthIn(min = 280.dp).fillMaxWidth())
        AppTextField(controller.formCnpj, controller::updateCnpj, label = "CNPJ", modifier = Modifier.fillMaxWidth())
        AppTextField(controller.formTipo, { controller.formTipo = it }, label = "Tipo", modifier = Modifier.fillMaxWidth(), placeholder = "Ex.: FORNECEDOR")
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
            AppButton("Cancelar", controller::closeForm, variant = PortalButtonVariant.Text)
            AppButton("Salvar", controller::save, enabled = controller.formValid && !controller.busy)
        }
    }
}

private fun formatCnpj(value: String): String {
    val digits = value.filter(Char::isDigit).take(14)
    return buildString {
        digits.forEachIndexed { index, digit ->
            if (index == 2 || index == 5) append('.')
            if (index == 8) append('/')
            if (index == 12) append('-')
            append(digit)
        }
    }
}
